import React from 'react';
import styled from 'styled-components';

import Btn from '../../buttons/Btn';
import { colors, spacing } from '../tokens';
import { OrderInfo } from '../types';
import {
  Actions, ErrorBanner, Panel, Reassurance, StepText, StepTitle,
} from './styles';

const Recap = styled.dl`
  margin: ${spacing.lg} 0;
  font-size: 14px;
  line-height: 1.45;
`;

const RecapRow = styled.div<{ isTotal?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: ${spacing.md};
  padding: ${spacing.xs} 0;
  ${({ isTotal }) => isTotal && `
    margin-top: ${spacing.sm};
    padding-top: ${spacing.sm};
    border-top: 1px solid ${colors.border};
  `}

  dt {
    color: ${({ isTotal }) => (isTotal ? colors.text : colors.textMuted)};
    font-weight: ${({ isTotal }) => (isTotal ? 700 : 400)};
  }

  dd {
    margin: 0;
    color: ${colors.text};
    font-weight: ${({ isTotal }) => (isTotal ? 700 : 600)};
    text-align: right;
    white-space: nowrap;
  }
`;

const CancelledNotice = styled.p`
  font-size: 14px;
  color: ${colors.text};
  background-color: ${colors.brandSoft};
  border-radius: 4px;
  padding: ${spacing.sm} ${spacing.md};
  margin: 0 0 ${spacing.lg};
`;

type PaymentStepProps = {
  order: OrderInfo;
  demarcheName: string;
  isPaying: boolean;
  paymentErrorMessage: string | null;
  wasCancelled?: boolean;
  onPay: () => void;
  onBack?: () => void;
};

// Step 5 (spec §5.10): not a form. "Payer" asks the backend for a Stripe Checkout session and
// navigates the whole page there — no card data ever touches this page (implementation-plan.md F3).
const PaymentStep: React.FC<PaymentStepProps> = ({
  order,
  demarcheName,
  isPaying,
  paymentErrorMessage,
  wasCancelled = false,
  onPay,
  onBack,
}) => (
  <Panel>
    <StepTitle>Paiement</StepTitle>
    <StepText>
      {`Dernière étape avant l’envoi de votre dossier « ${demarcheName} » à notre équipe : le règlement `
        + 'de l’accompagnement. Le prix affiché est définitif pour la prestation VroomVroom.'}
    </StepText>
    <Recap>
      <RecapRow>
        <dt>{`Accompagnement ${demarcheName}`}</dt>
        <dd>{order.offerSnapshot.priceLabel}</dd>
      </RecapRow>
      <RecapRow>
        <dt>Frais officiels éventuels (timbre fiscal, examens…)</dt>
        <dd>Non inclus</dd>
      </RecapRow>
      <RecapRow isTotal>
        <dt>Total à régler aujourd’hui</dt>
        <dd>{order.offerSnapshot.priceLabel}</dd>
      </RecapRow>
    </Recap>
    {wasCancelled && (
      <CancelledNotice>
        Le paiement a été annulé. Vos réponses ont bien été enregistrées, vous pouvez réessayer.
      </CancelledNotice>
    )}
    <Btn
      type="button"
      name="demarche-funnel-pay"
      text="Payer"
      block
      isLoading={isPaying}
      disabled={isPaying}
      onClick={onPay}
    />
    {paymentErrorMessage && <ErrorBanner>{paymentErrorMessage}</ErrorBanner>}
    <Reassurance>Paiement sécurisé via Stripe.</Reassurance>
    <Reassurance>VroomVroom est un service privé indépendant de France Titres — ANTS.</Reassurance>
    {onBack && (
      <Actions>
        <Btn type="button" name="demarche-funnel-payment-back" text="Retour" outlined block onClick={onBack} />
      </Actions>
    )}
  </Panel>
);

export { PaymentStep };
